import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLanguage,
  FaUserTag,
  FaClock,
  FaMoneyBillWave,
  FaInfoCircle,
  FaCheckCircle,
  FaHandsHelping,
  FaUserFriends,
} from "react-icons/fa";
import config, { getUserCity, fetchRemoteConfig } from "../../../config";
import TermsAcceptStep from "./TermsAcceptStep";

const BASE_URL = config.API_BASE_URL || "http://localhost:8000";

// Requirements — grouped by care type, catch-all care needs that don't
// fit Baby/Elder/Patient/Nurse.
export const GENERAL_CARE_GROUPS = [
  {
    group: "Personal Assistance",
    options: [
      { value: "bathing", label: "Bathing" },
      { value: "dressing", label: "Dressing" },
      { value: "grooming", label: "Grooming" },
      { value: "toileting", label: "Toileting" },
      { value: "feeding", label: "Feeding" },
      { value: "walking", label: "Walking" },
      { value: "personal-hygiene", label: "Personal Hygiene" },
    ],
  },
  {
    group: "Daily Activities",
    options: [
      { value: "meal-serving", label: "Meal Serving" },
      { value: "meal-assistance", label: "Meal Assistance" },
      { value: "daily-routine", label: "Daily Routine" },
      { value: "bed-making", label: "Bed Making" },
      { value: "personal-area-organization", label: "Personal Area Organization" },
      { value: "laundry", label: "Laundry" },
    ],
  },
  {
    group: "Household Support",
    options: [
      { value: "light-housekeeping", label: "Light Housekeeping" },
      { value: "personal-area-cleaning", label: "Personal Area Cleaning" },
      { value: "organizing-belongings", label: "Organizing Belongings" },
      { value: "basic-household-assistance", label: "Basic Household Assistance" },
    ],
  },
  {
    group: "Outside Assistance",
    options: [
      { value: "shopping", label: "Shopping" },
      { value: "errands", label: "Errands" },
      { value: "outside-accompaniment", label: "Outside Accompaniment" },
      { value: "doctor-appointment", label: "Doctor Appointment" },
      { value: "hospital-accompaniment", label: "Hospital Accompaniment" },
    ],
  },
  {
    group: "Companionship",
    options: [
      { value: "conversation", label: "Conversation" },
      { value: "general-company", label: "General Company" },
      { value: "daily-activity-assistance", label: "Daily Activity Assistance" },
    ],
  },
];

const RELATIONSHIP_OPTIONS = ["Self", "Parent", "Grandparent", "Spouse", "Child", "Relative", "Other"];

const GeneralCaretaker = () => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitError,  setSubmitError]  = useState("");
  const [step, setStep] = useState("form"); // "form" -> "terms"

  const categoryKey = "care-services";
  const serviceKey  = "general-caretaker";
  // City/Area come from the admin-configured backend list (config_data
  // collection), not a hardcoded array — see the same pattern's comment
  // in ElderCare.js for why this re-syncs on mount instead of trusting
  // the module-level default.
  const [areaOptions, setAreaOptions] = useState(config.AREA_OPTIONS || {});
  useEffect(() => {
    fetchRemoteConfig().then(() => setAreaOptions({ ...config.AREA_OPTIONS }));
  }, []);

  const selectedCategory = config.SERVICE_CONFIG?.[categoryKey];
  const selectedService  = config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey];
  const serviceLabel     = selectedService?.label || "General Caretaker";
  const categoryLabel    = selectedCategory?.label || "Care Services";

  const [formData, setFormData] = useState({
    // Requester's own contact details
    username: "", mobilenumber: "", email: "", address: "",
    city: "", area: "", language: "",

    // Person Details — who the care is for
    personName: "", personAge: "", personGender: "",
    relationship: "", reasonForAssistance: "",

    // Care services required — every listed service is included automatically
    // (see the "no selection" change below), so this starts pre-populated.
    service_type: GENERAL_CARE_GROUPS.flatMap((g) => g.options).map((o) => o.value),

    // Timing & caretaker preference
    gender: "", urgencyStatus: "", shiftTime: "", shiftDuration: "", salaryOffered: "",
  });

  const availableAreas = useMemo(() => {
    const sel = areaOptions[formData.city] || areaOptions[formData.city?.toLowerCase()];
    if (Array.isArray(sel)) return sel;
    if (sel?.areas) return sel.areas;
    return [];
  }, [formData.city, areaOptions]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const cleanValue = name === "mobilenumber" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setFormData((prev) => ({ ...prev, [name]: cleanValue, ...(name === "city" ? { area: "" } : {}) }));
  };

  // Step 1 -> Step 2: validate the form, then show Terms & Conditions.
  // The actual POST only happens from submitRequirement(), reached via the
  // Terms step's checkbox + Submit button — see TermsAcceptStep.
  const handleNext = (e) => {
    e.preventDefault();
    setSubmitError("");
    setStep("terms");
  };

  const submitRequirement = async () => {
    setSubmitError("");

    const detailedServiceList = GENERAL_CARE_GROUPS.flatMap((g) =>
      g.options
        .filter((item) => formData.service_type.includes(item.value))
        .map((item) => ({
          type: "general-care", group: g.group, key: item.value, value: item.value, label: item.label,
        }))
    );

    const requestBody = {
      // Requester's own contact details
      username: formData.username,
      mobilenumber: formData.mobilenumber,
      email: formData.email,
      address: formData.address,
      city: formData.city,
      area: formData.area,
      language: formData.language,

      // Person Details — who the care is for
      personName: formData.personName,
      personAge: formData.personAge,
      personGender: formData.personGender,
      relationship: formData.relationship,
      reasonForAssistance: formData.reasonForAssistance,

      // Care services required
      service_list: formData.service_type,
      detailed_service_list: detailedServiceList,
      service_type: "general-care-services",

      // Timing & caretaker preference
      gender: formData.gender,
      urgencyStatus: formData.urgencyStatus,
      shiftTime: formData.shiftTime,
      shiftDuration: formData.shiftDuration,
      salaryOffered: formData.salaryOffered,

      service_category: categoryKey,
      selected_city: getUserCity(),
      service: serviceKey,
      category_label: categoryLabel,
      service_label: serviceLabel,
      terms_accepted: true,
    };

    try {
      setIsSubmitting(true);
      const res = await fetch(`${BASE_URL}/api/requirements/general-caretaker/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json();

      if (res.ok && data?.success !== false) {
        setShowThankYou(true);
        setStep("form");
        setFormData({
          username: "", mobilenumber: "", email: "", address: "", city: "", area: "", language: "",
          personName: "", personAge: "", personGender: "", relationship: "", reasonForAssistance: "",
          service_type: GENERAL_CARE_GROUPS.flatMap((g) => g.options).map((o) => o.value),
          gender: "", urgencyStatus: "", shiftTime: "", shiftDuration: "", salaryOffered: "",
        });
      } else {
        setSubmitError(data?.message || data?.error || "Failed to submit requirement.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      setSubmitError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = {
    page:        { minHeight:"100vh", background:"linear-gradient(135deg,#f5f3ff 0%,#ede9fe 50%,#f5f3ff 100%)", padding:"30px 20px", fontFamily:"Segoe UI,Tahoma,Geneva,Verdana,sans-serif" },
    wrapper:     { maxWidth:"1580px", width:"96%", margin:"0 auto" },
    hero:        { background:"linear-gradient(135deg,#312e81,#4f46e5,#818cf8)", color:"#fff", borderRadius:"28px", padding:"34px 36px", boxShadow:"0 20px 40px rgba(79,70,229,0.22)", marginBottom:"24px" },
    heroBadge:   { display:"inline-block", background:"rgba(255,255,255,0.14)", padding:"7px 14px", borderRadius:"999px", fontSize:"12px", fontWeight:"700", marginBottom:"12px" },
    heroTitle:   { margin:0, fontSize:"2.1rem", fontWeight:"800" },
    heroText:    { marginTop:"10px", marginBottom:0, color:"rgba(255,255,255,0.9)", maxWidth:"900px", lineHeight:1.6, fontSize:"15px" },
    formCard:    { background:"#fff", borderRadius:"28px", border:"1px solid #ddd6fe", boxShadow:"0 14px 34px rgba(15,23,42,0.08)", overflow:"hidden", width:"100%", maxWidth:"1320px", margin:"0 auto" },
    formHeader:  { padding:"28px 40px", borderBottom:"1px solid #ddd6fe", background:"linear-gradient(180deg,#ffffff,#f5f3ff)" },
    formTitle:   { margin:0, fontSize:"1.6rem", fontWeight:"800", color:"#0f172a" },
    formSub:     { margin:"8px 0 0", color:"#64748b", fontSize:"14px" },
    body:        { padding:"32px 40px 36px" },
    section:     { marginBottom:"28px", padding:"24px", borderRadius:"20px", background:"#f5f3ff", border:"1px solid #ddd6fe" },
    sectionTitle:{ margin:"0 0 18px", fontSize:"1.1rem", fontWeight:"800", color:"#0f172a", display:"flex", alignItems:"center", gap:"8px" },
    grid:        { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:"20px", alignItems:"start" },
    field:       { display:"flex", flexDirection:"column", gap:"8px" },
    label:       { fontSize:"14px", fontWeight:"700", color:"#334155", display:"flex", alignItems:"center", gap:"8px" },
    input:       { width:"100%", padding:"14px 16px", borderRadius:"14px", border:"1px solid #d6deeb", fontSize:"15px", outline:"none", background:"#fff", boxSizing:"border-box" },
    select:      { width:"100%", padding:"14px 16px", borderRadius:"14px", border:"1px solid #d6deeb", fontSize:"15px", outline:"none", background:"#fff", boxSizing:"border-box" },
    textarea:    { width:"100%", minHeight:"120px", padding:"14px 16px", borderRadius:"14px", border:"1px solid #d6deeb", fontSize:"15px", outline:"none", background:"#fff", boxSizing:"border-box", resize:"vertical" },
    submitWrap:  { display:"flex", justifyContent:"center", marginTop:"30px" },
    submitBtn:   { border:"none", borderRadius:"999px", background:"linear-gradient(135deg,#4f46e5,#6366f1)", color:"#fff", padding:"15px 34px", fontSize:"15px", fontWeight:"800", minWidth:"260px", boxShadow:"0 12px 24px rgba(79,70,229,0.24)", cursor:"pointer" },
    helper:      { color:"#64748b", fontSize:"13px", margin:0, lineHeight:1.6 },
    serviceGrid: { display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:"10px", marginTop:"10px" },
    serviceCell: { display:"flex", alignItems:"center", gap:"8px", padding:"10px 14px", borderRadius:"12px", border:"1px solid #ddd6fe", background:"#fff" },
    serviceCellLabel:{ fontSize:"14px", fontWeight:"600", color:"#0f172a" },
    groupHeading:{ fontSize:"13px", fontWeight:"800", color:"#4f46e5", textTransform:"uppercase", letterSpacing:"0.04em", marginTop:"16px", marginBottom:"8px" },
    errorText:   { color:"#dc2626", fontSize:"14px", fontWeight:"600", marginTop:"12px" },
    popupOverlay:{ position:"fixed", inset:0, backgroundColor:"rgba(15,23,42,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:50 },
    popupCard:   { background:"#fff", borderRadius:"18px", padding:"22px 24px", maxWidth:"360px", width:"90%", textAlign:"center", boxShadow:"0 20px 40px rgba(15,23,42,0.25)" },
    popupTitle:  { margin:"10px 0 6px", fontSize:"1.1rem", fontWeight:"800", color:"#312e81" },
    popupText:   { margin:0, fontSize:"14px", color:"#4b5563" },
    popupButton: { marginTop:"16px", borderRadius:"999px", border:"none", background:"#4f46e5", color:"#fff", fontSize:"14px", fontWeight:"700", padding:"10px 24px", cursor:"pointer" },
  };

  return (
    <>
      {showThankYou && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupCard}>
            <FaCheckCircle color="#4f46e5" size={32}/>
            <h3 style={styles.popupTitle}>Thank you!</h3>
            <p style={styles.popupText}>Your requirement has been submitted. We will get back to you shortly.</p>
            <button type="button" style={styles.popupButton} onClick={() => { setShowThankYou(false); navigate("/"); }}>OK</button>
          </div>
        </div>
      )}

      <div style={styles.page}>
        <div style={styles.wrapper}>

          {/* Hero */}
          <div style={styles.hero}>
            <div style={styles.heroBadge}>🤝 {serviceLabel}</div>
            <h1 style={styles.heroTitle}>General Caretaker Support</h1>
            <p style={styles.heroText}>
              For care needs that don't fit a specific category — companionship, housekeeping support,
              errands, and more.
            </p>
          </div>

          {step === "form" && (
          <form style={styles.formCard} onSubmit={handleNext}>
              <div style={styles.formHeader}>
                <h2 style={styles.formTitle}>General Caretaker Requirement Form</h2>
                <p style={styles.formSub}>Fill in your contact details, describe the care needed, and mention your salary offer.</p>
              </div>

              <div style={styles.body}>
                {/* Your Contact Details */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaUser color="#4f46e5"/> Your Contact Details</h3>
                  <div style={styles.grid}>
                    {[
                      { id:"username",     label:"Your Name",     icon:<FaUser/>,          type:"text",  required:true },
                      { id:"mobilenumber", label:"Mobile Number",  icon:<FaPhoneAlt/>,      type:"tel",   required:true, pattern:"[0-9]{10}", minLength:10, maxLength:10 },
                      { id:"email",        label:"Email",          icon:<FaEnvelope/>,      type:"email", required:true },
                      { id:"address",      label:"Address",        icon:<FaMapMarkerAlt/>,  type:"text",  required:true },
                    ].map((f) => (
                      <div key={f.id} style={styles.field}>
                        <label htmlFor={f.id} style={styles.label}>{f.icon} {f.label}</label>
                        <input id={f.id} style={styles.input} type={f.type} name={f.id}
                          value={formData[f.id]} onChange={handleInputChange}
                          required={f.required} pattern={f.pattern}
                          minLength={f.minLength} maxLength={f.maxLength}
                        />
                      </div>
                    ))}

                    {/* City */}
                    <div style={styles.field}>
                      <label htmlFor="city" style={styles.label}><FaMapMarkerAlt/> City</label>
                      <select id="city" style={styles.select} name="city" value={formData.city} onChange={handleInputChange} required>
                        <option value="">Select City</option>
                        {Object.keys(areaOptions).map((c) => (
                          <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                        ))}
                      </select>
                    </div>

                    {/* Area */}
                    <div style={styles.field}>
                      <label htmlFor="area" style={styles.label}><FaMapMarkerAlt/> Area</label>
                      <select id="area" style={styles.select} name="area" value={formData.area} onChange={handleInputChange} required disabled={!formData.city}>
                        <option value="">Select Area</option>
                        {availableAreas.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </div>

                    {/* Language */}
                    <div style={styles.field}>
                      <label htmlFor="language" style={styles.label}><FaLanguage/> Language</label>
                      <select id="language" style={styles.select} name="language" value={formData.language} onChange={handleInputChange} required>
                        <option value="">Select Language</option>
                        {["Tamil","Telugu","Kannada","Malayalam","Hindi","English"].map((l) => (
                          <option key={l} value={l.toLowerCase()}>{l}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Person Details */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaUserFriends color="#4f46e5"/> Person Details</h3>
                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="personName" style={styles.label}><FaUser/> Name</label>
                      <input id="personName" style={styles.input} type="text" name="personName"
                        value={formData.personName} onChange={handleInputChange} required
                      />
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="personAge" style={styles.label}><FaUser/> Age</label>
                      <input id="personAge" style={styles.input} type="number" name="personAge"
                        min="0" max="120" value={formData.personAge} onChange={handleInputChange} required
                      />
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="personGender" style={styles.label}><FaUserTag/> Gender</label>
                      <select id="personGender" style={styles.select} name="personGender" value={formData.personGender} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Others">Others</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="relationship" style={styles.label}><FaUserTag/> Relationship</label>
                      <select id="relationship" style={styles.select} name="relationship" value={formData.relationship} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        {RELATIONSHIP_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                  </div>

                  <div style={{ marginTop: "20px" }}>
                    <label htmlFor="reasonForAssistance" style={styles.label}><FaInfoCircle/> Reason for Assistance</label>
                    <textarea id="reasonForAssistance" style={styles.textarea} name="reasonForAssistance"
                      value={formData.reasonForAssistance} onChange={handleInputChange}
                      placeholder="Tell us why support is needed, especially if it doesn't fit the services listed below."
                      required
                    />
                  </div>
                </div>

                {/* Requirements */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaHandsHelping color="#4f46e5"/> Services Included</h3>
                  <p style={styles.helper}>Every service below, across all care types, is included for this booking.</p>

                  {GENERAL_CARE_GROUPS.map((g) => (
                    <div key={g.group}>
                      <div style={styles.groupHeading}>{g.group}</div>
                      <div style={styles.serviceGrid}>
                        {g.options.map((opt) => (
                          <div key={opt.value} style={styles.serviceCell}>
                            <FaCheckCircle color="#4f46e5" size={14} />
                            <span style={styles.serviceCellLabel}>{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {submitError && <p style={styles.errorText}>{submitError}</p>}
                </div>

                {/* Timing & Caretaker Preference */}
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}><FaClock color="#4f46e5"/> Timing & Caretaker Preference</h3>
                  <div style={styles.grid}>
                    <div style={styles.field}>
                      <label htmlFor="gender" style={styles.label}><FaUserTag/> Preferred Caretaker Gender</label>
                      <select id="gender" style={styles.select} name="gender" value={formData.gender} onChange={handleInputChange} required>
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="any">Any</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="urgencyStatus" style={styles.label}><FaInfoCircle/> Urgency</label>
                      <select id="urgencyStatus" style={styles.select} name="urgencyStatus" value={formData.urgencyStatus} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="within_days">Within Days</option>
                        <option value="within_months">Within Months</option>
                        <option value="just_enquiry">Just Enquiry</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="shiftTime" style={styles.label}><FaClock/> Shift</label>
                      <select id="shiftTime" style={styles.select} name="shiftTime" value={formData.shiftTime} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="morning">Morning</option>
                        <option value="afternoon">Afternoon</option>
                        <option value="evening">Evening</option>
                        <option value="full_day">Full Day</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="shiftDuration" style={styles.label}><FaClock/> Hours</label>
                      <select id="shiftDuration" style={styles.select} name="shiftDuration" value={formData.shiftDuration} onChange={handleInputChange} required>
                        <option value="">Select</option>
                        <option value="2hrs">2 Hours</option>
                        <option value="4hrs">4 Hours</option>
                        <option value="6hrs">6 Hours</option>
                        <option value="12hrs">12 Hours</option>
                        <option value="live_24hrs">Live-in (24 Hours)</option>
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label htmlFor="salaryOffered" style={styles.label}><FaMoneyBillWave/> Salary Offered (₹)</label>
                      <input id="salaryOffered" style={styles.input} type="number" name="salaryOffered"
                        value={formData.salaryOffered} onChange={handleInputChange} placeholder="e.g. 15000" required
                      />
                    </div>
                  </div>
                </div>

                <div style={styles.submitWrap}>
                  <button type="submit" style={styles.submitBtn} disabled={isSubmitting}>
                    Next
                  </button>
                </div>
              </div>
            </form>
          )}

          {step === "terms" && (
            <div style={styles.formCard}>
              <TermsAcceptStep
                onBack={() => setStep("form")}
                onSubmit={submitRequirement}
                isSubmitting={isSubmitting}
                submitError={submitError}
                serviceLabel={serviceLabel}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GeneralCaretaker;
